/* ═══════════════════════════════════════════════════════════════════════════
   SPIDER-VERSE MOTION SYSTEM V55 - "INSTANT LOAD EDITION"
   All V54 effects preserved - scroll-reveal animations removed for reliability

   ═══ FEATURES ═══
   - Velocity-based chromatic aberration (scroll speed → RGB split intensity)
   - Gyroscopic parallax (mobile DeviceOrientation API)
   - Magnetic cursor with touch support
   - Liquid gradient background with organic motion
   - Impact frames on interactions
   - Energy particles on key interactions
   - Variable font "breathing" effect

   ═══ V55 CHANGES ═══
   - Scroll-triggered panel reveals REMOVED — all sections visible on load
   - Text scramble triggers immediately instead of on scroll
   - Fixes blank sections on slow devices / poor connections
   - All V54 reliability fixes retained (rAF lifecycle, visibility API, etc.)

   ═══ V54 RELIABILITY FIXES (retained) ═══
   - All rAF loops pause when offscreen or tab hidden (battery saver)
   - Cursor trail uses DOM update instead of innerHTML rebuild per frame
   - Chromatic aberration rAF race condition fixed
   - Impact frames have safety timeout cleanup
   - Text scramble uses pre-created spans instead of innerHTML per frame
   - Canvas resizes only when particles active
   - Page Visibility API integration for all loops
   - Safari/Firefox CSS compatibility (backdrop-filter, conic-gradient)
   - DeviceOrientation defensive guards for all browsers

   Performance: 60fps, GPU-accelerated, respects prefers-reduced-motion
   ═══════════════════════════════════════════════════════════════════════════ */

(() => {
  'use strict';

  // Respect user motion preferences
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isTouch = 'ontouchstart' in window;
  const hasGyro = 'DeviceOrientationEvent' in window;

  // Track page visibility for pausing all loops
  let pageHidden = false;

  // Page Visibility API - pause all loops when tab is hidden
  document.addEventListener('visibilitychange', () => {
    pageHidden = document.hidden;
  });

  // Throttle for performance
  const throttle = (fn, delay) => {
    let last = 0;
    return (...args) => {
      const now = Date.now();
      if (now - last >= delay) {
        last = now;
        fn(...args);
      }
    };
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 1. VELOCITY-BASED CHROMATIC ABERRATION
  // RGB split intensity tied to scroll speed - the faster you scroll, the more "glitchy"
  // ═══════════════════════════════════════════════════════════════════════════
  const initVelocityChromatic = () => {
    if (prefersReducedMotion) return;

    const chromaticElements = document.querySelectorAll('.chromatic-img, .hero__product-image');
    if (!chromaticElements.length) return;

    let lastScroll = window.scrollY;
    let velocity = 0;
    let rafId = null;

    const updateChromatic = () => {
      const currentScroll = window.scrollY;
      const delta = currentScroll - lastScroll;

      // Calculate velocity with easing
      velocity += (Math.abs(delta) - velocity) * 0.1;
      velocity = Math.min(velocity, 30); // Cap max velocity

      // Apply chromatic split based on velocity
      const splitAmount = velocity * 0.5;
      const direction = delta > 0 ? 1 : -1;

      chromaticElements.forEach(el => {
        if (velocity > 0.5) {
          el.style.setProperty('--chromatic-x', `${splitAmount * direction}px`);
          el.style.setProperty('--chromatic-y', `${splitAmount * 0.3}px`);
          el.style.setProperty('--chromatic-intensity', Math.min(velocity / 10, 1));
        } else {
          el.style.setProperty('--chromatic-x', '0px');
          el.style.setProperty('--chromatic-y', '0px');
          el.style.setProperty('--chromatic-intensity', '0');
        }
      });

      lastScroll = currentScroll;
      velocity *= 0.95; // Decay

      if (velocity > 0.1) {
        rafId = requestAnimationFrame(updateChromatic);
      } else {
        rafId = null; // Chain ended - allow new one on next scroll
      }
    };

    window.addEventListener('scroll', throttle(() => {
      if (!rafId) {
        rafId = requestAnimationFrame(updateChromatic);
      }
    }, 16), { passive: true });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 2. GYROSCOPIC PARALLAX (Mobile) - Tilt your phone to move layers
  // ═══════════════════════════════════════════════════════════════════════════
  const initGyroParallax = () => {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (prefersReducedMotion || !isMobile || !hasGyro) return;

    const parallaxLayers = document.querySelectorAll('[data-gyro-parallax]');
    const hero = document.querySelector('.hero');
    if (!parallaxLayers.length && !hero) return;

    let tiltX = 0, tiltY = 0;
    let targetX = 0, targetY = 0;
    let permissionGranted = false;
    let gyroRafId = null;
    let heroVisible = true;

    const handleOrientation = (e) => {
      if (!permissionGranted) return;

      const beta = Math.max(-45, Math.min(45, e.beta || 0));
      const gamma = Math.max(-45, Math.min(45, e.gamma || 0));

      targetX = gamma / 45;
      targetY = beta / 45;
    };

    const animateGyro = () => {
      if (pageHidden || !heroVisible) {
        gyroRafId = null;
        return;
      }

      tiltX += (targetX - tiltX) * 0.08;
      tiltY += (targetY - tiltY) * 0.08;

      if (hero) {
        const bgLayer = hero.querySelector('.hero__bg-layer');
        const burstLayer = hero.querySelector('.hero__burst-layer');
        const halftone = hero.querySelector('.hero__halftone');

        if (bgLayer) bgLayer.style.transform = `translate(${tiltX * 15}px, ${tiltY * 10}px)`;
        if (burstLayer) burstLayer.style.transform = `translate(${tiltX * -20}px, ${tiltY * -15}px) rotate(${tiltX * 5}deg)`;
        if (halftone) halftone.style.transform = `translate(${tiltX * 10}px, ${tiltY * 8}px)`;
      }

      parallaxLayers.forEach(el => {
        const depth = parseFloat(el.dataset.gyroParallax) || 1;
        el.style.transform = `translate(${tiltX * 20 * depth}px, ${tiltY * 15 * depth}px)`;
      });

      gyroRafId = requestAnimationFrame(animateGyro);
    };

    // Pause when hero scrolls out of view
    if (hero) {
      const heroObserver = new IntersectionObserver(([entry]) => {
        heroVisible = entry.isIntersecting;
        if (heroVisible && permissionGranted && !gyroRafId && !pageHidden) {
          gyroRafId = requestAnimationFrame(animateGyro);
        }
      }, { threshold: 0 });
      heroObserver.observe(hero);
    }

    const requestGyroPermission = () => {
      if (typeof DeviceOrientationEvent !== 'undefined' &&
          typeof DeviceOrientationEvent.requestPermission === 'function') {
        DeviceOrientationEvent.requestPermission()
          .then(state => {
            if (state === 'granted') {
              permissionGranted = true;
              window.addEventListener('deviceorientation', handleOrientation, { passive: true });
              gyroRafId = requestAnimationFrame(animateGyro);
            }
          })
          .catch(() => {});
      } else {
        permissionGranted = true;
        window.addEventListener('deviceorientation', handleOrientation, { passive: true });
        gyroRafId = requestAnimationFrame(animateGyro);
      }
    };

    document.addEventListener('touchstart', function initGyro() {
      requestGyroPermission();
      document.removeEventListener('touchstart', initGyro);
    }, { once: true, passive: true });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 3. MAGNETIC CURSOR - Premium interaction with touch support
  // Optimized: SVG trail uses DOM attribute updates instead of innerHTML per frame
  // Cursor loop pauses when mouse is idle
  // ═══════════════════════════════════════════════════════════════════════════
  const initMagneticCursor = () => {
    const isDesktop = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (prefersReducedMotion || !isDesktop) return;

    const dot = document.createElement('div');
    dot.className = 'cursor-dot';
    const ring = document.createElement('div');
    ring.className = 'cursor-ring';
    const trail = document.createElement('div');
    trail.className = 'cursor-trail';

    document.body.appendChild(dot);
    document.body.appendChild(ring);
    document.body.appendChild(trail);
    document.documentElement.classList.add('has-custom-cursor');

    let mouseX = -100, mouseY = -100;
    let dotX = mouseX, dotY = mouseY;
    let ringX = mouseX, ringY = mouseY;
    let trailPoints = [];
    const maxTrailPoints = 20;

    // Pre-create SVG and circle elements once (no DOM rebuilds per frame)
    const svgNS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNS, 'svg');
    svg.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;pointer-events:none;z-index:9998';
    trail.appendChild(svg);
    const trailCircles = [];
    for (let i = 0; i < maxTrailPoints; i++) {
      const c = document.createElementNS(svgNS, 'circle');
      c.setAttribute('r', String(2 + i * 0.1));
      c.style.display = 'none';
      svg.appendChild(c);
      trailCircles.push(c);
    }

    const magnets = document.querySelectorAll('[data-magnet], .btn-comic, .product-card, .nav-link');
    const lerp = (a, b, t) => a + (b - a) * t;

    let cursorRafId = null;
    let isIdle = false;

    window.addEventListener('pointermove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;

      trailPoints.push({ x: mouseX, y: mouseY, age: 0 });
      if (trailPoints.length > maxTrailPoints) trailPoints.shift();

      if (isIdle && !pageHidden) {
        isIdle = false;
        cursorRafId = requestAnimationFrame(animate);
      }
    }, { passive: true });

    window.addEventListener('pointerdown', () => {
      ring.classList.add('is-pressing');
      dot.classList.add('is-pressing');
    }, { passive: true });

    window.addEventListener('pointerup', () => {
      ring.classList.remove('is-pressing');
      dot.classList.remove('is-pressing');
    }, { passive: true });

    magnets.forEach(magnet => {
      magnet.addEventListener('pointerenter', () => {
        ring.classList.add('is-hovering');
        dot.classList.add('is-hovering');
      }, { passive: true });

      magnet.addEventListener('pointerleave', () => {
        ring.classList.remove('is-hovering');
        dot.classList.remove('is-hovering');
        magnet.style.transform = '';
      }, { passive: true });

      magnet.addEventListener('pointermove', (e) => {
        const rect = magnet.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const deltaX = e.clientX - centerX;
        const deltaY = e.clientY - centerY;

        const pullStrength = 0.3;
        magnet.style.transform = `translate(${deltaX * pullStrength}px, ${deltaY * pullStrength}px)`;
      }, { passive: true });
    });

    const animate = () => {
      if (pageHidden) {
        cursorRafId = null;
        return;
      }

      dotX = lerp(dotX, mouseX, 0.35);
      dotY = lerp(dotY, mouseY, 0.35);
      ringX = lerp(ringX, mouseX, 0.12);
      ringY = lerp(ringY, mouseY, 0.12);

      dot.style.transform = `translate3d(${dotX - 5}px, ${dotY - 5}px, 0)`;
      ring.style.transform = `translate3d(${ringX - 24}px, ${ringY - 24}px, 0)`;

      // Age out old trail points
      for (let i = trailPoints.length - 1; i >= 0; i--) {
        trailPoints[i].age++;
        if (trailPoints[i].age >= maxTrailPoints) {
          trailPoints.splice(i, 1);
        }
      }

      // Update trail circle attributes directly (no DOM rebuild)
      for (let i = 0; i < maxTrailPoints; i++) {
        if (i < trailPoints.length) {
          const p = trailPoints[i];
          const opacity = 1 - (p.age / maxTrailPoints);
          trailCircles[i].setAttribute('cx', p.x);
          trailCircles[i].setAttribute('cy', p.y);
          trailCircles[i].setAttribute('fill', 'rgba(233, 30, 144, ' + (opacity * 0.5) + ')');
          trailCircles[i].style.display = '';
        } else {
          trailCircles[i].style.display = 'none';
        }
      }

      // Go idle if mouse stopped and lerp converged
      const deltaX = Math.abs(dotX - mouseX);
      const deltaY = Math.abs(dotY - mouseY);
      if (deltaX < 0.5 && deltaY < 0.5 && trailPoints.length === 0) {
        isIdle = true;
        cursorRafId = null;
        return;
      }

      cursorRafId = requestAnimationFrame(animate);
    };

    cursorRafId = requestAnimationFrame(animate);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 4. LIQUID GRADIENT BACKGROUND - Living, breathing color
  // Optimized: Pauses when hero is offscreen, runs at 30fps (visually identical)
  // ═══════════════════════════════════════════════════════════════════════════
  const initLiquidGradient = () => {
    if (prefersReducedMotion) return;

    const hero = document.querySelector('.hero');
    if (!hero) return;

    const liquid = document.createElement('div');
    liquid.className = 'liquid-gradient';
    liquid.setAttribute('aria-hidden', 'true');
    hero.insertBefore(liquid, hero.firstChild);

    let time = 0;
    let liquidRafId = null;
    let heroVisible = true;
    let lastFrame = 0;
    const FRAME_INTERVAL = 33; // ~30fps is plenty for slow organic sine waves

    const animateLiquid = (timestamp) => {
      if (pageHidden || !heroVisible) {
        liquidRafId = null;
        return;
      }

      // Throttle to ~30fps
      if (timestamp - lastFrame < FRAME_INTERVAL) {
        liquidRafId = requestAnimationFrame(animateLiquid);
        return;
      }
      lastFrame = timestamp;

      time += 0.005;

      const x1 = 20 + Math.sin(time) * 15;
      const y1 = 30 + Math.cos(time * 0.7) * 20;
      const x2 = 80 + Math.sin(time * 0.8) * 15;
      const y2 = 40 + Math.cos(time * 0.5) * 20;
      const x3 = 50 + Math.sin(time * 0.6) * 20;
      const y3 = 70 + Math.cos(time * 0.9) * 15;

      liquid.style.background =
        'radial-gradient(ellipse 60vmax 50vmax at ' + x1 + '% ' + y1 + '%, rgba(233, 30, 144, 0.4), transparent 60%),' +
        'radial-gradient(ellipse 55vmax 45vmax at ' + x2 + '% ' + y2 + '%, rgba(0, 162, 232, 0.35), transparent 62%),' +
        'radial-gradient(ellipse 50vmax 40vmax at ' + x3 + '% ' + y3 + '%, rgba(255, 215, 0, 0.25), transparent 55%)';

      liquidRafId = requestAnimationFrame(animateLiquid);
    };

    // Pause when hero scrolls out of view
    const heroObserver = new IntersectionObserver(([entry]) => {
      heroVisible = entry.isIntersecting;
      if (heroVisible && !liquidRafId && !pageHidden) {
        liquidRafId = requestAnimationFrame(animateLiquid);
      }
    }, { threshold: 0 });
    heroObserver.observe(hero);

    liquidRafId = requestAnimationFrame(animateLiquid);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 5. IMPACT FRAMES - Comic "BAM!" moments on interactions
  // Optimized: Safety timeout prevents orphaned DOM elements
  // ═══════════════════════════════════════════════════════════════════════════
  const initImpactFrames = () => {
    if (prefersReducedMotion) return;

    const createImpactFrame = (x, y, type) => {
      type = type || 'default';
      const frame = document.createElement('div');
      frame.className = 'impact-frame impact-frame--' + type;
      frame.style.left = x + 'px';
      frame.style.top = y + 'px';

      const rotation = Math.random() * 30 - 15;
      frame.style.setProperty('--impact-rotation', rotation + 'deg');

      document.body.appendChild(frame);

      // Remove after animation with safety timeout fallback
      let removed = false;
      const cleanup = () => {
        if (!removed) {
          removed = true;
          if (frame.parentNode) frame.parentNode.removeChild(frame);
        }
      };
      frame.addEventListener('animationend', cleanup, { once: true });
      setTimeout(cleanup, 1000);
    };

    document.querySelectorAll('.btn-comic, .btn-comic--cart').forEach(btn => {
      btn.addEventListener('click', () => {
        const rect = btn.getBoundingClientRect();
        createImpactFrame(rect.left + rect.width / 2, rect.top + rect.height / 2, 'burst');
      });
    });

    if (!isTouch) {
      document.querySelectorAll('.product-card').forEach(card => {
        card.addEventListener('mouseenter', (e) => {
          createImpactFrame(e.clientX, e.clientY, 'spark');
        });
      });
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 6. SCROLL-TRIGGERED PANELING - V55: Immediately reveal all panels
  // Scroll observer removed for reliability on slow devices/connections.
  // All sections now load visible; classes added for CSS compatibility.
  // ═══════════════════════════════════════════════════════════════════════════
  const initScrollPaneling = () => {
    const panels = document.querySelectorAll('.section-wipe, .panel, .product-card');
    panels.forEach(panel => {
      panel.classList.add('panel-revealed');
    });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 7. ENERGY PARTICLES - Sparks on key interactions
  // Optimized: Canvas hidden when idle, efficient particle removal
  // ═══════════════════════════════════════════════════════════════════════════
  const initEnergyParticles = () => {
    if (prefersReducedMotion) return;

    const canvas = document.createElement('canvas');
    canvas.className = 'particle-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.display = 'none';
    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    const particles = [];
    const colors = ['#e91e90', '#00a2e8', '#ffd700', '#ff4500'];

    let animating = false;

    const animate = () => {
      if (particles.length === 0) {
        animating = false;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.style.display = 'none';
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Efficient compaction instead of splice per element
      let writeIdx = 0;
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.1;
        p.life -= 0.02;

        if (p.life <= 0) continue;

        particles[writeIdx++] = p;

        ctx.save();
        ctx.globalAlpha = p.life;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y - p.size);
        ctx.lineTo(p.x + p.size * 0.3, p.y);
        ctx.lineTo(p.x, p.y + p.size);
        ctx.lineTo(p.x - p.size * 0.3, p.y);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
      particles.length = writeIdx;

      requestAnimationFrame(animate);
    };

    const createParticle = (x, y, count) => {
      count = count || 8;
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
        const speed = 2 + Math.random() * 4;
        particles.push({
          x: x, y: y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 1,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: 3 + Math.random() * 5
        });
      }
    };

    const startAnimation = () => {
      if (!animating) {
        animating = true;
        canvas.style.display = '';
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        requestAnimationFrame(animate);
      }
    };

    window.addEventListener('resize', throttle(() => {
      if (animating) {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
    }, 200));

    document.querySelectorAll('.btn-comic').forEach(btn => {
      btn.addEventListener('click', () => {
        const rect = btn.getBoundingClientRect();
        createParticle(rect.left + rect.width / 2, rect.top + rect.height / 2, 15);
        startAnimation();
      });
    });

    window.createParticles = (x, y, count) => {
      createParticle(x, y, count);
      startAnimation();
    };
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 8. VARIABLE FONT BREATHING - Headlines pulse with life (GPU-optimized)
  // ═══════════════════════════════════════════════════════════════════════════
  const initFontBreathing = () => {
    if (prefersReducedMotion) return;

    const breathingElements = document.querySelectorAll('.hero__headline, .section-title');
    if (!breathingElements.length) return;

    breathingElements.forEach(el => {
      el.style.willChange = 'transform';
      el.classList.add('breathing-active');
    });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 9. TEXT SCRAMBLE EFFECT V2 - Enhanced with velocity
  // Optimized: Pre-creates span elements, updates textContent per frame
  // ═══════════════════════════════════════════════════════════════════════════
  const initTextScramble = () => {
    if (prefersReducedMotion) return;

    const scrambleEls = document.querySelectorAll('[data-scramble]');
    if (!scrambleEls.length) return;

    const glyphs = '\u25B2\u25C6\u25CF\u25A0\u2736\u2605\u2606#$%&*+=?@!';

    const scramble = (el) => {
      const finalText = el.getAttribute('data-scramble') || el.textContent.trim();
      const duration = 800;
      let start = null;

      // Pre-create spans once instead of rebuilding DOM every frame
      const spans = [];
      while (el.firstChild) el.removeChild(el.firstChild);
      for (let i = 0; i < finalText.length; i++) {
        const span = document.createElement('span');
        el.appendChild(span);
        spans.push(span);
      }

      const tick = (t) => {
        if (!start) start = t;
        const progress = Math.min(1, (t - start) / duration);
        const reveal = Math.floor(progress * finalText.length);

        for (let i = 0; i < finalText.length; i++) {
          if (i < reveal) {
            spans[i].textContent = finalText[i];
            spans[i].className = 'char-revealed';
          } else if (finalText[i] === ' ') {
            spans[i].textContent = ' ';
            spans[i].className = '';
          } else {
            const randomChar = glyphs[(Math.random() * glyphs.length) | 0];
            spans[i].textContent = randomChar;
            spans[i].className = 'char-scrambling';
          }
        }

        if (progress < 1) {
          requestAnimationFrame(tick);
        } else {
          el.textContent = finalText;
          el.classList.add('scramble-complete');
        }
      };

      el.classList.add('is-scrambling');
      requestAnimationFrame(tick);
    };

    // V55: Trigger scramble immediately instead of waiting for scroll.
    // The effect is cosmetic only (text is readable either way) but
    // firing on load ensures headings animate even on slow devices.
    scrambleEls.forEach((el, i) => {
      setTimeout(() => scramble(el), 150 + i * 200);
    });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 10. 3D CARD TILT - Enhanced with perspective and lighting
  // ═══════════════════════════════════════════════════════════════════════════
  const initCardTilt = () => {
    if (prefersReducedMotion || isTouch) return;

    const cards = document.querySelectorAll('.product-card');
    if (!cards.length) return;

    cards.forEach(card => {
      let rect = null;

      card.addEventListener('pointerenter', () => {
        rect = card.getBoundingClientRect();
        card.classList.add('is-tilting');
      }, { passive: true });

      card.addEventListener('pointerleave', () => {
        const inner = card.querySelector('.product-card__inner');
        if (inner) {
          inner.style.transform = '';
          inner.style.setProperty('--light-x', '50%');
          inner.style.setProperty('--light-y', '50%');
        }
        card.classList.remove('is-tilting');
      }, { passive: true });

      card.addEventListener('pointermove', (e) => {
        if (!rect) return;

        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        const rotateX = (0.5 - y) * 20;
        const rotateY = (x - 0.5) * 20;
        const translateZ = 30;

        const inner = card.querySelector('.product-card__inner');
        if (inner) {
          inner.style.transform =
            'perspective(1200px) rotateX(' + rotateX + 'deg) rotateY(' + rotateY + 'deg) translateZ(' + translateZ + 'px)';
          inner.style.setProperty('--light-x', (x * 100) + '%');
          inner.style.setProperty('--light-y', (y * 100) + '%');
        }

        card.style.setProperty('--mouse-x', (x * 100) + '%');
        card.style.setProperty('--mouse-y', (y * 100) + '%');
      }, { passive: true });
    });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 11. MOBILE TAP FEEDBACK - Touch interactions that feel premium
  // ═══════════════════════════════════════════════════════════════════════════
  const initMobileTapFeedback = () => {
    if (!isTouch) return;

    const tappables = document.querySelectorAll('.btn-comic, .product-card, .nav-link, .mobile-nav__link');

    tappables.forEach(el => {
      el.addEventListener('touchstart', (e) => {
        el.classList.add('is-tapped');

        const rect = el.getBoundingClientRect();
        const ripple = document.createElement('span');
        ripple.className = 'tap-ripple';
        ripple.style.left = (e.touches[0].clientX - rect.left) + 'px';
        ripple.style.top = (e.touches[0].clientY - rect.top) + 'px';
        el.appendChild(ripple);

        setTimeout(() => {
          if (ripple.parentNode) ripple.parentNode.removeChild(ripple);
        }, 600);
      }, { passive: true });

      el.addEventListener('touchend', () => {
        el.classList.remove('is-tapped');
      }, { passive: true });
    });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 12. SCROLL PROGRESS INDICATOR - Animated progress bar
  // ═══════════════════════════════════════════════════════════════════════════
  const initScrollProgress = () => {
    if (prefersReducedMotion) return;

    const progress = document.createElement('div');
    progress.className = 'scroll-progress';
    progress.setAttribute('aria-hidden', 'true');
    document.body.appendChild(progress);

    const updateProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const percent = (scrollTop / docHeight) * 100;
      progress.style.width = percent + '%';
    };

    window.addEventListener('scroll', throttle(updateProgress, 16), { passive: true });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 13. HERO ENTRANCE ANIMATION - Dramatic page load
  // ═══════════════════════════════════════════════════════════════════════════
  const initHeroEntrance = () => {
    const hero = document.querySelector('.hero');
    if (!hero) return;

    setTimeout(() => {
      hero.classList.add('hero-entered');
      document.body.classList.add('page-loaded');

      const headline = hero.querySelector('.hero__headline');
      if (headline) {
        headline.classList.add('headline-entered');
      }
    }, 100);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // INIT ALL SYSTEMS
  // ═══════════════════════════════════════════════════════════════════════════
  const init = () => {
    initHeroEntrance();
    initVelocityChromatic();
    initGyroParallax();
    initLiquidGradient();
    initMagneticCursor();
    initImpactFrames();
    initScrollPaneling();
    initEnergyParticles();
    initFontBreathing();
    initTextScramble();
    initCardTilt();
    initMobileTapFeedback();
    initScrollProgress();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
